import React, { useState, useEffect } from 'react';
import { Card, Form, Select, Switch, Col, Row, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

export interface TableConfig {
    fields: string[];
    groupBy: string | null;
    aggregateChildren: boolean;
}

const DEFAULT_TABLE_CONFIG: TableConfig = {
    fields: [],
    groupBy: null,
    aggregateChildren: true,
};

interface Props {
    initialConfig?: Partial<TableConfig>;
    fieldOptions?: Array<{ label: string; value: string }>;
    onChange: (config: TableConfig) => void;
}

export const SectionConfigTable: React.FC<Props> = ({ initialConfig, fieldOptions, onChange }) => {
    const [config, setConfig] = useState<TableConfig>({
        ...DEFAULT_TABLE_CONFIG,
        ...initialConfig,
    });

    const updateField = (field: keyof TableConfig, value: any) => {
        const newConfig = { ...config, [field]: value };
        setConfig(newConfig);
        onChange(newConfig);  // Call onChange directly to avoid infinite loop
    };

    return (
        <Card size="small" style={{ background: '#f5f5f5' }}>
            <Form.Item 
                label={
                    <span>
                        KPI fields to show{' '}
                        <Tooltip title="Select which statistics to display in table columns (e.g., average, min, max, count)">
                            <InfoCircleOutlined style={{ color: '#999' }} />
                        </Tooltip>
                    </span>
                }
            >
                <Select
                    mode="multiple"
                    allowClear
                    options={fieldOptions || []}
                    value={config.fields}
                    onChange={(vals) => updateField('fields', vals)}
                    placeholder="Select fields to display"
                />
            </Form.Item>
            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item 
                        label={
                            <span>
                                Group by{' '}
                                <Tooltip title="How to organize data rows: by child areas, patterns, or individual measurement points">
                                    <InfoCircleOutlined style={{ color: '#999' }} />
                                </Tooltip>
                            </span>
                        }
                    >
                        <Select
                            allowClear
                            value={config.groupBy ?? undefined}
                            onChange={(val) => updateField('groupBy', val ?? null)}
                            options={[
                                { label: 'Child area', value: 'childArea' },
                                { label: 'Pattern', value: 'pattern' },
                                { label: 'Pattern point', value: 'patternPoint' },
                            ]}
                            placeholder="Select grouping"
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item 
                        label={
                            <span>
                                Aggregate children{' '}
                                <Tooltip title="When ON: Combines multiple measurements per group into single aggregated row. When OFF: Shows individual measurements as separate rows">
                                    <InfoCircleOutlined style={{ color: '#999' }} />
                                </Tooltip>
                            </span>
                        }
                    >
                        <Switch
                            checked={config.aggregateChildren}
                            onChange={(checked) => updateField('aggregateChildren', checked)}
                        />
                    </Form.Item>
                </Col>
            </Row>
        </Card>
    );
};

export default SectionConfigTable;
